/**
 * KollectCare AI - Authentication Store
 * Zustand state management for auth
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../utils/api';

export const useStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      workspace: null,
      isLoading: false,
      error: null,

      // Actions
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login({ email, password });
          const { user, token } = response.data;
          
          localStorage.setItem('token', token);
          set({ user, token, isLoading: false });
          
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.error || 'Login failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.register(data);
          const { user, token } = response.data;
          
          localStorage.setItem('token', token);
          set({ user, token, isLoading: false });
          
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.error || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, workspace: null, error: null });
      },

      setUser: (user) => {
        set({ user });
      },

      setWorkspace: (workspace) => {
        set({ workspace });
      },

      fetchCurrentUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
          const response = await authAPI.me();
          set({ user: response.data.user });
        } catch (error) {
          get().logout();
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'kollectcare-auth',
      partialize: (state) => ({ user: state.user, token: state.token }),
    }
  )
);

// UI State Store
export const useUIStore = create((set) => ({
  sidebarCollapsed: false,
  sidebarOpen: true,
  theme: 'light',
  
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    set({ theme });
  },
}));

// Notifications Store
export const useNotificationStore = create((set, get) => ({
  notifications: [],
  
  addNotification: (notification) => {
    const id = Date.now();
    const newNotification = { id, ...notification };
    set((state) => ({ notifications: [...state.notifications, newNotification] }));
    
    // Auto remove after 5 seconds
    if (notification.autoClose !== false) {
      setTimeout(() => {
        get().removeNotification(id);
      }, notification.duration || 5000);
    }
    
    return id;
  },
  
  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },
  
  clearAll: () => set({ notifications: [] }),
}));
